// React
import { useMemo } from "react";

// Third-party
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Hooks & Contexts
import { useAuth } from "@/contexts";

// Components
import {
  AuthCard,
  LanguageSwitcher,
  Header,
  HEADER_HEIGHT,
} from "@/components";

// Constants
import { AppRoutes } from "@/constants";

interface LoginLocationState {
  error?: string;
}

export function LoginPage() {
  const location = useLocation();
  const { authStatus } = useAuth();

  const stateError = (location.state as LoginLocationState | null)?.error;

  const initialMode = useMemo(() => {
    if (
      stateError === "NEEDS_REGISTRATION" ||
      authStatus === "needs_registration"
    )
      return "register";
    return "login";
  }, [authStatus, stateError]);

  const initialErrorKey = useMemo(() => {
    if (
      stateError === "NEEDS_REGISTRATION" ||
      authStatus === "needs_registration"
    )
      return "registrationRequired";
    if (stateError === "NOT_VERIFIED") return "notVerified";
    if (stateError === "FORBIDDEN") return "forbidden";
    return undefined;
  }, [authStatus, stateError]);

  // Back button for header
  const backButton = (
    <Link
      to={AppRoutes.home}
      className="flex items-center text-gray-500 hover:text-gray-900 transition-colors p-2 rounded-lg hover:bg-gray-100"
    >
      <ArrowLeft className="w-5 h-5" />
    </Link>
  );

  return (
    <>
      {/* Header - fixed at top, outside main container to avoid transform issues */}
      <Header
        position="fixed"
        variant="transparent"
        leftContent={backButton}
        showLogo={false}
        showAuthButton={false}
        rightContent={<LanguageSwitcher />}
      />

      {/* Main page container */}
      <div className="min-h-screen flex flex-col bg-white relative">
        {/* Animated background - behind content but visible */}
        <div className="absolute top-0 left-0 w-full h-full bg-white flex items-center justify-center pointer-events-none">
          <div className="w-[30rem] h-[30rem] bg-red-500 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-blob"></div>
        </div>

        {/* Main content - centered with padding for fixed header */}
        <div
          className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative z-10"
          style={{ paddingTop: HEADER_HEIGHT }}
        >
          <AuthCard
            initialMode={initialMode}
            initialErrorKey={initialErrorKey}
          />
        </div>
      </div>
    </>
  );
}
